import { breakLines, calculateTextHeight } from './drawCanvas'
import { type SnapLine, getMultiSelectionBounds } from './interactions'
import { type LayoutNode, runsToString } from '@backend/lib/db/typeRoots/layoutNode'

type MarqueeRect = { start: { x: number; y: number }; current: { x: number; y: number } }

// Colors for selection states
const COLORS = {
  default: 'rgba(90, 154, 250, 1)', // Blue for regular selection
  grouped: 'rgba(34, 197, 94, 1)', // Green for grouped items
}

// Check if all selected nodes share the same groupId
const areNodesGrouped = (nodes: LayoutNode[], indices: number[]): boolean => {
  if (indices.length < 2) return false
  const selectedNodes = indices.map((i) => nodes[i]).filter(Boolean)
  if (selectedNodes.length < 2) return false

  const firstGroupId = selectedNodes[0]?.groupId
  if (!firstGroupId) return false

  return selectedNodes.every((node) => node?.groupId === firstGroupId)
}

export default (
  nodes: LayoutNode[],
  ctx: CanvasRenderingContext2D,
  selectedIndices: number[],
  hoveredIndex: number,
  snapLines: SnapLine[],
  marquee?: MarqueeRect,
) => {
  const selectedSet = new Set(selectedIndices)
  const isGrouped = areNodesGrouped(nodes, selectedIndices)
  const selectionColor = isGrouped ? COLORS.grouped : COLORS.default

  nodes
    .filter((node) => node.visible)
    .forEach((node, i) => {
      const isSelected = selectedSet.has(i)
      const isHovered = i === hoveredIndex

      if (isSelected || isHovered) {
        ctx.save()

        applyRotation(ctx, node)

        // Use green color for grouped items, blue otherwise
        const nodeColor = isSelected && isGrouped ? COLORS.grouped : COLORS.default
        drawBorder(node, ctx, nodeColor)

        if (node.data.type === 'text' && !node.data.isLoading) {
          drawUnderlines(node, ctx, nodeColor)
        }

        // Only draw individual handles for single selection. Locked nodes can't be
        // resized or rotated, so omit the handles — the selection outline still shows.
        if (isSelected && selectedIndices.length === 1 && !node.locked) {
          drawCorners(node, ctx, nodeColor)
          drawRotationHandle(node, ctx, nodeColor)
        }

        ctx.restore()
      }
    })

  // Draw multi-selection bounding box with handles
  if (selectedIndices.length > 1) {
    const bounds = getMultiSelectionBounds(nodes, selectedIndices)
    if (bounds) {
      drawMultiSelectionBox(ctx, bounds, isGrouped, selectionColor)
    }
  }

  // Draw marquee selection rectangle
  if (marquee) {
    drawMarquee(ctx, marquee)
  }

  if (snapLines.length > 0) {
    drawSnapTargets(ctx, nodes, snapLines)
    drawSnapLines(ctx, snapLines)
  }
}

/** Outline the node(s) supplying the active snap so the user can see what
 *  they're aligning to — the lines alone don't disambiguate which element in
 *  a dense scene is the target. Canvas-border snap lines have no source and
 *  are skipped. Drawn before drawSnapLines so the dashed lines paint on top. */
const drawSnapTargets = (
  ctx: CanvasRenderingContext2D,
  nodes: LayoutNode[],
  snapLines: SnapLine[],
) => {
  const indices = new Set<number>()
  for (const line of snapLines) {
    if (line.sourceNodeIndex !== undefined) indices.add(line.sourceNodeIndex)
  }
  if (indices.size === 0) return

  for (const index of indices) {
    const node = nodes[index]
    if (!node || !node.visible) continue
    ctx.save()
    applyRotation(ctx, node)
    ctx.strokeStyle = '#0096ff'
    ctx.lineWidth = 1.5
    ctx.strokeRect(node.x, node.y, node.width, node.height)
    ctx.restore()
  }
}

const drawMultiSelectionBox = (
  ctx: CanvasRenderingContext2D,
  bounds: { x: number; y: number; width: number; height: number },
  isGrouped: boolean,
  color: string,
) => {
  // Draw border around all selected items
  // Dashed only when grouped, solid when just multi-selected
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  if (isGrouped) {
    ctx.setLineDash([5, 5])
  }
  ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height)
  ctx.setLineDash([])

  // Draw corner handles
  ctx.fillStyle = 'white'
  ctx.strokeStyle = color
  ctx.lineWidth = 2

  const cornerRadius = 8

  const corners = [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
  ]

  corners.forEach((corner) => {
    ctx.beginPath()
    ctx.arc(corner.x, corner.y, cornerRadius, 0, 2 * Math.PI)
    ctx.fill()
    ctx.stroke()
  })
}

const drawMarquee = (ctx: CanvasRenderingContext2D, marquee: MarqueeRect) => {
  const { start, current } = marquee
  const x = Math.min(start.x, current.x)
  const y = Math.min(start.y, current.y)
  const width = Math.abs(current.x - start.x)
  const height = Math.abs(current.y - start.y)

  // Fill with semi-transparent blue
  ctx.fillStyle = 'rgba(90, 154, 250, 0.1)'
  ctx.fillRect(x, y, width, height)

  // Dashed border
  ctx.strokeStyle = 'rgba(90, 154, 250, 0.8)'
  ctx.lineWidth = 1
  ctx.setLineDash([4, 4])
  ctx.strokeRect(x, y, width, height)
  ctx.setLineDash([])
}

const drawBorder = (node: LayoutNode, ctx: CanvasRenderingContext2D, color: string) => {
  ctx.strokeStyle = color
  ctx.lineWidth = 3
  ctx.strokeRect(node.x, node.y, node.width, node.height)
}

const drawCorners = (node: LayoutNode, ctx: CanvasRenderingContext2D, color: string) => {
  ctx.fillStyle = 'white'
  ctx.strokeStyle = color
  ctx.lineWidth = 2

  const cornerRadius = 8

  const corners = [
    { x: node.x, y: node.y },
    { x: node.x + node.width, y: node.y },
    { x: node.x, y: node.y + node.height },
    { x: node.x + node.width, y: node.y + node.height },
  ]

  corners.forEach((corner) => {
    ctx.beginPath()
    ctx.arc(corner.x, corner.y, cornerRadius, 0, 2 * Math.PI)
    ctx.fill()
    ctx.stroke()
  })
}

const drawRotationHandle = (node: LayoutNode, ctx: CanvasRenderingContext2D, color: string) => {
  const handleX = node.x + node.width / 2
  const handleY = node.y - 40

  // line
  ctx.beginPath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.moveTo(handleX, node.y)
  ctx.lineTo(handleX, handleY + 9)
  ctx.stroke()

  const radius = 10

  // Draw the circular arc
  ctx.beginPath()
  ctx.arc(handleX, handleY, radius, 0.5 * Math.PI, 2.0 * Math.PI)
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.stroke()

  // Draw the arrowhead
  const arrowAngle = 2.0 * Math.PI
  const arrowX = handleX + radius * Math.cos(arrowAngle)
  const arrowY = handleY + radius * Math.sin(arrowAngle)
  const headLength = 8

  ctx.beginPath()
  ctx.moveTo(arrowX, arrowY)
  ctx.lineTo(
    arrowX - headLength * Math.cos(arrowAngle - Math.PI / -10),
    arrowY - headLength * Math.sin(arrowAngle - Math.PI / -10),
  )
  ctx.moveTo(arrowX, arrowY)
  ctx.lineTo(
    arrowX - headLength * Math.cos(arrowAngle + Math.PI / 1.5),
    arrowY - headLength * Math.sin(arrowAngle + Math.PI / 1.5),
  )
  ctx.stroke()
}

const drawUnderlines = (node: LayoutNode, ctx: CanvasRenderingContext2D, color: string) => {
  const { options, content } = node.data as Extract<LayoutNode['data'], { type: 'text' }>
  const {
    fontFamily,
    fontWeight,
    fontStyle,
    fontSize,
    lineHeight,
    horizontalAlign,
    verticalAlign,
  } = options

  // Fast path: when the node has captured per-line layout, mirror it so the
  // underlines hug exactly what's painted (including any inline-styled run
  // widths and padding offsets the LLM produced).
  if (options.preWrappedLines && options.preWrappedLines.length > 0) {
    ctx.lineWidth = 1
    ctx.strokeStyle = color
    for (const line of options.preWrappedLines) {
      const x = node.x + line.x
      const y = node.y + line.y + line.height
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + line.width, y)
      ctx.stroke()
    }
    return
  }

  // Slow path mirrors drawCanvas's slow path: respect padding and the same
  // verticalAlign/textHeight math so underlines line up with rendered text.
  const padTop = options.paddingTop ?? 0
  const padRight = options.paddingRight ?? 0
  const padBottom = options.paddingBottom ?? 0
  const padLeft = options.paddingLeft ?? 0
  const innerX = node.x + padLeft
  const innerY = node.y + padTop
  const innerW = Math.max(0, node.width - padLeft - padRight)
  const innerH = Math.max(0, node.height - padTop - padBottom)

  const lines = breakLines(
    runsToString(content),
    innerW,
    ctx,
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
  )
  const textHeight = calculateTextHeight(lines, fontSize, lineHeight)

  const y =
    verticalAlign === 'center'
      ? innerY + innerH / 2 - textHeight / 2
      : verticalAlign === 'bottom'
        ? innerY + innerH - textHeight
        : innerY

  lines.forEach((line, i) => {
    const underlineWidth = ctx.measureText(line).width
    const x =
      horizontalAlign === 'center'
        ? innerX + innerW / 2 - underlineWidth / 2
        : horizontalAlign === 'right'
          ? innerX + innerW - underlineWidth
          : innerX

    ctx.beginPath()
    ctx.moveTo(x, y + (i + 1) * lineHeight * fontSize)
    ctx.lineTo(x + underlineWidth, y + (i + 1) * lineHeight * fontSize)
    ctx.lineWidth = 1
    ctx.strokeStyle = color
    ctx.stroke()
  })
}

const drawSnapLines = (ctx: CanvasRenderingContext2D, snapLines: SnapLine[]) => {
  const EXTENT = 10000

  ctx.beginPath()
  ctx.strokeStyle = '#0096ff'
  ctx.setLineDash([5, 5])
  ctx.lineWidth = 2

  snapLines.forEach((line) => {
    if (line.type === 'vertical') {
      ctx.moveTo(line.position, -EXTENT)
      ctx.lineTo(line.position, EXTENT)
    } else {
      ctx.moveTo(-EXTENT, line.position)
      ctx.lineTo(EXTENT, line.position)
    }
  })

  ctx.stroke()
  ctx.setLineDash([])
}

export const drawVirtualBorder = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  ctx.strokeStyle = 'rgba(128, 128, 128, 0.4)'
  ctx.lineWidth = 3
  ctx.strokeRect(0, 0, width, height)
}

export const applyRotation = (ctx: CanvasRenderingContext2D, node: LayoutNode) => {
  const centerX = node.x + node.width / 2
  const centerY = node.y + node.height / 2

  ctx.translate(centerX, centerY)
  ctx.rotate((node.rotation * Math.PI) / 180)
  ctx.translate(-centerX, -centerY)
}
